import React, { FC, ReactNode, useEffect, useState } from 'react';
import styled from 'styles/styled-components';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import translations from 'translations';
import accountMessages from 'utils/messages';
import mainLayoutReducer from '../MainLayout/reducer';
import mainLayoutSaga from '../MainLayout/saga';
import OpenAppBanner from 'components/OpenAppBanner';
import Header from './Header';
import Footer from './Footer';
import { HeaderThemeProvider } from './HeaderThemeContext';
import iconArrowUp from './icon-arrow-up.svg';

const Main = styled.main`
  min-height: calc(100vh - 400px);
  margin-top: 65px;
`;

const BackToTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 12px;
  border: none;
  border-radius: 100px;
  background: #ffffff;
  box-shadow: 2px 6px 32px 0px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 0.3s ease,
    background-color 0.2s ease;
  &:hover {
    background: #f0f9f1;
  }
  &:active {
    background: #d4edd5;
  }
`;

const BackToTopIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const BACK_TO_TOP_SHOW_RATIO = 1;

const useBackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > window.innerHeight * BACK_TO_TOP_SHOW_RATIO);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return isVisible;
};

interface Props {
  children: ReactNode;
}

const LandingLayout: FC<Props> = ({ children }) => {
  const isBackToTopVisible = useBackToTop();

  return (
    <HeaderThemeProvider>
      <OpenAppBanner green={false} appPath="">
        <Header />
      </OpenAppBanner>
      <Main>{children}</Main>
      <Footer />
      <BackToTopButton
        type="button"
        $visible={isBackToTopVisible}
        aria-label={translations(accountMessages.backToTop)}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <BackToTopIcon src={iconArrowUp} alt="" />
      </BackToTopButton>
    </HeaderThemeProvider>
  );
};

const withReducer = injectReducer<Props>({ key: 'main', reducer: mainLayoutReducer });
const withSaga = injectSaga<Props>({ key: 'main', saga: mainLayoutSaga });

export default withReducer(withSaga(LandingLayout));
